import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  signal,
  Signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  BoelsCategoryHierarchy,
  CmsNavigationComponent,
  CmsNavigationNode,
  UserEnum,
} from '@boels/models';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { GenericLinkComponent } from '../generic-link/generic-link.component';
import { faArrowLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { NavigationStart, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { ToggleLanguageSelectorComponent } from '../toggle-language-selector/toggle-language-selector.component';
import { ToggleUserTypeComponent } from '../toggle-user-type/toggle-user-type.component';
import { BoelsSkeletonComponent } from '../skeleton/boels-skeleton.component';
import { TranslocoModule } from '@jsverse/transloco';
import { UserDataFacade } from '@boels/shared-state';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { animate, style, transition, trigger } from '@angular/animations';
import { OverlayModule } from '@angular/cdk/overlay';
import {
  CdkScrollable,
  ScrollDispatcher,
  ScrollingModule,
  ViewportRuler,
} from '@angular/cdk/scrolling';

@Component({
  selector: 'boels-navigation-mobile',
  templateUrl: './navigation-mobile.component.html',
  styleUrls: ['./navigation-mobile.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    GenericLinkComponent,
    ToggleLanguageSelectorComponent,
    ToggleUserTypeComponent,
    BoelsSkeletonComponent,
    TranslocoModule,
    OverlayModule,
    ScrollingModule,
  ],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)', opacity: 0 }),
        animate(
          '200ms ease-out',
          style({ transform: 'translateX(0)', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        animate(
          '250ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ transform: 'translateX(-100%)', opacity: 0 }),
        ),
      ]),
    ]),
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('250ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0 })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationMobileComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('closeButton') closeButton!: ElementRef;
  @ViewChild('navigationContent') navigationContent!: ElementRef<HTMLElement>;
  @ViewChild(CdkScrollable) scrollable!: CdkScrollable;

  // Injects
  private destroyRef = inject(DestroyRef);
  private renderer = inject(Renderer2);
  private router = inject(Router);
  private scrollDispatcher = inject(ScrollDispatcher);
  private userDataFacade = inject(UserDataFacade);
  private viewportRuler = inject(ViewportRuler);

  // Inputs
  closeModuleEvent = output<boolean>();
  isActive = input<boolean>(false);
  navigationData = input<CmsNavigationComponent | undefined>();
  showCountrySelect = input<boolean>(true);
  showUserType = input<boolean>(true);

  // Signals
  activeNode = signal<Array<any> | undefined>([]);
  navigatedLevel = signal<number>(0);
  headerTitle = signal<string | undefined>(undefined);
  headerUrl = signal<string | undefined>(undefined);
  isAnimating = signal(false);
  lastFocusedElement = signal<HTMLElement | null>(null);
  mainContent = signal<HTMLElement | null>(null);
  previousNodeList = signal<
    Array<{
      title?: string;
      url?: string;
      children: Array<any>;
    }>
  >([]);
  userLoggedIn: Signal<boolean> = toSignal(
    this.userDataFacade.userData().pipe(
      filter(Boolean),
      map((user) => user.type === UserEnum.B2C_AUTHENTICATED),
    ),
    { initialValue: false },
  );

  // IconDefinitions
  protected readonly faChevronRight = faChevronRight;

  @HostBinding('class.is-visible') get isVisible() {
    return this.isActive();
  }

  ngOnInit() {
    this.initializeData();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.closeModule();
      });

    if (this.isActive()) {
      this.disableMainContent();
    }

    this.mainContent.set(
      document.querySelector('main') ||
        document.querySelector('#root') ||
        document.querySelector('.app-root'),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['navigationData']) {
      this.initializeData();
    }
    if (changes['isActive']) {
      const isActive = changes['isActive'].currentValue;
      if (isActive) {
        this.disableMainContent();
      } else {
        this.enableMainContent();
      }
    }
  }

  ngAfterViewInit() {
    if (this.isActive() && this.closeButton) {
      setTimeout(() => {
        this.closeButton.nativeElement.focus();
      });
    }

    this.setupScrollHandling();
  }

  private setupScrollHandling() {
    if (!this.scrollable || !this.navigationContent) return;

    this.scrollDispatcher
      .scrolled(100)
      .pipe(
        filter(
          () => !!this.scrollable && !this.isAnimating() && this.isActive(),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        try {
          const element = this.navigationContent.nativeElement;
          const scrollPosition = this.scrollable.measureScrollOffset('top');
          const viewportSize = this.viewportRuler.getViewportSize();
          const scrollHeight = element.scrollHeight;

          // Check if we're at the top
          if (scrollPosition === 0) {
            this.triggerAnimation(element, 'overscroll-top');
          }
          // Check if we're at the bottom with better precision
          else if (
            Math.abs(scrollPosition + viewportSize.height - scrollHeight) <= 1
          ) {
            this.triggerAnimation(element, 'overscroll-bottom');
          }
        } catch (error) {
          console.warn('Error in scroll handling:', error);
        }
      });
  }

  private triggerAnimation(element: HTMLElement, className: string) {
    if (this.isAnimating() || !this.isActive()) return;

    this.isAnimating.set(true);
    this.renderer.addClass(element, className);

    setTimeout(() => {
      if (this.isActive()) {
        this.renderer.removeClass(element, className);
        setTimeout(() => {
          this.isAnimating.set(false);
        }, 100);
      }
    }, 500);
  }

  navigateToPage(url: string) {
    this.closeModule();
    this.router.navigateByUrl(url);
  }

  closeModule(clearData = true): void {
    if (clearData) {
      this.updateHeaderContent();
      this.navigatedLevel.set(0);
      this.previousNodeList.set([]);
    }
    this.enableMainContent();
    this.initializeData();
    this.closeModuleEvent.emit(false);
    // Restore focus to the last focused element
    if (this.lastFocusedElement() !== null) {
      this.lastFocusedElement()!.focus();
      this.lastFocusedElement.set(null);
    }
  }

  navigateCategory(
    navigationNode: CmsNavigationNode,
    resolvedData?: any,
  ): void {
    const currentActiveNode = this.activeNode();

    if (resolvedData?.category) {
      const category = resolvedData.category;

      this.registerNavigationPath(
        1,
        category.name,
        category.url,
        currentActiveNode,
      );

      if (category.subcategories?.length) {
        this.activeNode.set(category.subcategories);
      }

      return;
    }

    if (
      navigationNode.children?.length ||
      navigationNode.pages?.pages?.length
    ) {
      this.registerNavigationPath(
        1,
        navigationNode.title,
        resolvedData?.component?.url || undefined,
        currentActiveNode,
      );

      this.activeNode.set(
        navigationNode.pages?.pages || navigationNode.children,
      );
    }
  }

  getNavObject(
    title: string | undefined,
    hasChildren: boolean,
    url: string | undefined,
  ): object {
    return {
      title,
      hasChildren,
      url,
    };
  }

  navigateBack(): void {
    this.registerNavigationPath(-1);
  }

  getCmsNavigationNodeCategory(
    nav: CmsNavigationNode,
    resolvedData: {
      category?: BoelsCategoryHierarchy;
      component?: { url: string };
    },
  ): string {
    if (resolvedData?.category?.name) {
      return resolvedData.category.name;
    }
    if (resolvedData?.component && nav.title) {
      return nav.title;
    }
    return 'Category not found';
  }

  getCmsNavigationNodeUrl(
    nav: CmsNavigationNode,
    resolvedData: {
      category?: BoelsCategoryHierarchy;
      component?: { url: string };
    },
  ): string {
    if (resolvedData?.category?.url) return resolvedData.category.url;
    if (resolvedData?.component?.url) return resolvedData.component.url;
    if (nav.pages?.pages?.[0]?.url) return nav.pages.pages[0].url;
    if (nav.entries?.[0]?.itemType === 'CMSLinkComponent') {
      return resolvedData?.component?.url || '';
    }
    return '';
  }

  getHasChildrenCategory(
    nav: CmsNavigationNode,
    resolvedData: unknown,
  ): boolean {
    const typedData = resolvedData as {
      category?: BoelsCategoryHierarchy;
      component?: { url: string };
    };
    if (!typedData) return false;
    if (nav?.children?.length || nav?.pages?.pages?.length) return true;
    const subcategories = typedData.category?.subcategories;
    return Array.isArray(subcategories) && subcategories.length > 0;
  }

  private initializeData() {
    this.activeNode.set(this.navigationData()?.navigationNode?.children);
  }

  private disableMainContent(): void {
    // Store the currently focused element before opening the modal
    this.lastFocusedElement.set(document.activeElement as HTMLElement);

    this.renderer.addClass(document.body, 'modal-open');
    const scrollY = window.scrollY;
    this.renderer.setStyle(document.body, 'position', 'fixed');
    this.renderer.setStyle(document.body, 'top', `-${scrollY}px`);
    this.renderer.setStyle(document.body, 'width', '100%');

    // Set inert on the main content instead of aria-hidden on body
    if (this.mainContent()) {
      this.renderer.setAttribute(this.mainContent(), 'inert', '');
    }
  }

  private enableMainContent(): void {
    this.renderer.removeClass(document.body, 'modal-open');
    const scrollY = document.body.style.top;
    this.renderer.removeStyle(document.body, 'position');
    this.renderer.removeStyle(document.body, 'top');
    this.renderer.removeStyle(document.body, 'width');
    window.scrollTo(0, parseInt(scrollY || '0') * -1);

    // Remove inert from the main content
    if (this.mainContent()) {
      this.renderer.removeAttribute(this.mainContent(), 'inert');
    }
  }

  private registerNavigationPath(
    direction: number,
    headerTitle?: string,
    headerUrl?: string,
    activeNode?: Array<any>,
  ): void {
    // Early return if there is no active node
    if (!this.activeNode()) return;

    if (direction < 0) {
      // Navigate back: get the last node from the previous node list
      const previousNodeList = this.previousNodeList();
      const targetObj = previousNodeList[previousNodeList.length - 1];
      this.activeNode.set(targetObj.children);
      this.previousNodeList().pop();
      this.updateHeaderContent(targetObj.title, targetObj.url);
      const navigationLevel = Math.max(this.navigatedLevel() - 1, 0);
      this.navigatedLevel.set(navigationLevel);
    } else if (direction > 0) {
      // Navigate forward: create a history object and update the previous node list
      const historyObject = {
        title: this.headerTitle(),
        url: this.headerUrl(),
        children: activeNode || [],
      };
      this.updateHeaderContent(headerTitle, headerUrl);
      this.previousNodeList.update((list) => [...list, historyObject]);
      this.navigatedLevel.set(this.navigatedLevel() + 1);
    }
  }

  private updateHeaderContent(title?: string, url?: string): void {
    this.headerUrl.set(url);
    this.headerTitle.set(title);
  }

  ngOnDestroy() {
    this.enableMainContent();
  }

  protected readonly faArrowLeft = faArrowLeft;
}
